import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Users, Wifi, Tv, Wind, Mountain, Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { roomService } from "../../services/roomService";
import { imgUrl } from "../../utils/imgUrl";
import { useViewport } from "../../hooks/useViewport";

const FALLBACK = "https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=800&h=600&fit=crop";

// ── Per-card image carousel ───────────────────────────────────────────────────
const RoomCarousel = ({ images, roomName, onOpenLightbox }) => {
  const [idx, setIdx] = useState(0);
  const imgs = images?.length ? images : [null];
  const total = imgs.length;

  const prev = (e) => { e.stopPropagation(); setIdx(i => (i - 1 + total) % total); };
  const next = (e) => { e.stopPropagation(); setIdx(i => (i + 1) % total); };

  return (
    <figure style={{ position: "relative", height: 224, overflow: "hidden", margin: 0, cursor: total > 1 ? "zoom-in" : "default" }}
      onClick={() => onOpenLightbox(idx)}>
      <img
        src={imgUrl(imgs[idx]) || FALLBACK}
        alt={`${roomName} ${idx + 1}`}
        style={{ width: "100%", height: "100%", objectFit: "cover", transition: "opacity 0.3s" }}
      />

      {/* Arrows — only when multiple images */}
      {total > 1 && (
        <>
          <button onClick={prev} style={arrowStyle("left")}>
            <ChevronLeft size={18} />
          </button>
          <button onClick={next} style={arrowStyle("right")}>
            <ChevronRight size={18} />
          </button>

          {/* Dots */}
          <div style={{ position: "absolute", bottom: 10, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 5 }}>
            {imgs.map((_, i) => (
              <span key={i} onClick={e => { e.stopPropagation(); setIdx(i); }}
                style={{ width: i === idx ? 18 : 6, height: 6, borderRadius: 3, backgroundColor: i === idx ? "#fff" : "rgba(255,255,255,0.5)", transition: "all 0.25s", cursor: "pointer" }} />
            ))}
          </div>

          {/* Counter badge */}
          <span style={{ position: "absolute", top: 50, right: 12, backgroundColor: "rgba(0,0,0,0.5)", color: "#fff", fontSize: 11, padding: "2px 8px", borderRadius: 9999 }}>
            {idx + 1}/{total}
          </span>
        </>
      )}
    </figure>
  );
};

const arrowStyle = (side) => ({
  position: "absolute", top: "50%", [side]: 8, transform: "translateY(-50%)",
  width: 30, height: 30, borderRadius: "50%", border: "none",
  backgroundColor: "rgba(0,0,0,0.45)", color: "#fff",
  display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer", zIndex: 2,
});

// ── Fullscreen lightbox ───────────────────────────────────────────────────────
const Lightbox = ({ images, startIdx, roomName, onClose }) => {
  const [idx, setIdx] = useState(startIdx);
  const imgs = images?.length ? images : [null];
  const total = imgs.length;

  const prev = useCallback(() => setIdx(i => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIdx(i => (i + 1) % total), [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, onClose]);

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.92)", zIndex: 9999,
               display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>

      {/* Close */}
      <button onClick={onClose}
        style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.15)", border: "none",
                 color: "#fff", borderRadius: "50%", width: 40, height: 40, cursor: "pointer",
                 display: "flex", alignItems: "center", justifyContent: "center" }}>
        <X size={20} />
      </button>

      {/* Counter */}
      <p style={{ position: "absolute", top: 20, left: "50%", transform: "translateX(-50%)",
                  color: "rgba(255,255,255,0.7)", fontSize: 13 }}>
        {roomName} &nbsp;·&nbsp; {idx + 1} / {total}
      </p>

      {/* Image */}
      <div onClick={e => e.stopPropagation()}
        style={{ position: "relative", maxWidth: "90vw", maxHeight: "80vh", display: "flex", alignItems: "center" }}>
        <img
          src={imgUrl(imgs[idx]) || FALLBACK}
          alt={`${roomName} ${idx + 1}`}
          style={{ maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain", borderRadius: 8, boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }}
        />

        {total > 1 && (
          <>
            <button onClick={prev}
              style={{ ...arrowStyle("left"), left: -44, backgroundColor: "rgba(255,255,255,0.15)", width: 36, height: 36 }}>
              <ChevronLeft size={20} />
            </button>
            <button onClick={next}
              style={{ ...arrowStyle("right"), right: -44, backgroundColor: "rgba(255,255,255,0.15)", width: 36, height: 36 }}>
              <ChevronRight size={20} />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {total > 1 && (
        <div style={{ display: "flex", gap: 8, marginTop: 20, overflowX: "auto", maxWidth: "90vw", padding: "0 8px" }}>
          {imgs.map((img, i) => (
            <img key={i} src={imgUrl(img) || FALLBACK} alt={`thumb ${i + 1}`}
              onClick={e => { e.stopPropagation(); setIdx(i); }}
              style={{ width: 60, height: 44, objectFit: "cover", borderRadius: 6, cursor: "pointer",
                       opacity: i === idx ? 1 : 0.45, border: i === idx ? "2px solid #16a34a" : "2px solid transparent",
                       transition: "all 0.2s", flexShrink: 0 }} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: "all",    label: "All Rooms" },
  { value: "luxury", label: "Luxury" },
  { value: "deluxe", label: "Deluxe" },
];

const CATEGORY_COLORS = {
  luxury: { bg: "#fdf4ff", color: "#7e22ce" },
  deluxe: { bg: "#eff6ff", color: "#1d4ed8" },
};

const Rooms = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const { isMobile } = useViewport();

  useEffect(() => {
    roomService.getAllRooms()
      .then(res => setRooms(res.data.rooms || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filteredRooms = rooms.filter(r => {
    const matchCat    = categoryFilter === "all" || r.category === categoryFilter;
    const matchStatus = statusFilter   === "all" || r.status   === statusFilter;
    return matchCat && matchStatus;
  });

  // Only show category tabs that have at least one room
  const activeCategories = CATEGORIES.filter(c =>
    c.value === "all" || rooms.some(r => r.category === c.value)
  );

  const amenities = [
    { icon: <Wifi size={28} />, text: "High-Speed WiFi" },
    { icon: <Tv size={28} />, text: "LED TV" },
    { icon: <Wind size={28} />, text: "Hot Water" },
    { icon: <Mountain size={28} />, text: "Mountain Views" },
  ];

  const sectionStyle = { padding: "64px 24px" };
  const containerStyle = { maxWidth: 1200, margin: "0 auto" };
  const cardStyle = { backgroundColor: "#fff", borderRadius: 20, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", overflow: "hidden", transition: "transform 0.3s, box-shadow 0.3s" };
  const btnPrimaryStyle = { backgroundColor: "#16a34a", color: "#fff", padding: "8px 24px", borderRadius: 9999, fontWeight: 600, textDecoration: "none", display: "inline-block", border: "none", cursor: "pointer", boxShadow: "0 2px 8px rgba(22,163,74,0.3)", transition: "transform 0.2s", fontSize: 13 };
  const btnFilterStyle = (active) => ({ padding: "8px 24px", borderRadius: 9999, fontWeight: 600, fontSize: 13, cursor: "pointer", border: active ? "none" : "1.5px solid #e5e7eb", backgroundColor: active ? "#16a34a" : "transparent", color: active ? "#fff" : "#374151", boxShadow: active ? "0 2px 8px rgba(22,163,74,0.3)" : "none", transition: "all 0.2s" });

  return (
    <div>
      {/* Lightbox */}
      {lightbox && (
        <Lightbox
          images={lightbox.room.images}
          startIdx={lightbox.startIdx}
          roomName={lightbox.room.name}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Hero */}
      <section style={{ height: 384, display: "flex", alignItems: "center", justifyContent: "center", backgroundImage: "url(https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=1920&h=800&fit=crop)", backgroundSize: "cover", backgroundPosition: "center", position: "relative" }}>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(22,163,74,0.8) 0%, rgba(21,128,61,0.9) 100%)" }} />
        <div style={{ position: "relative", zIndex: 10, textAlign: "center", color: "#fff", padding: "0 24px", animation: "fadeInUp 0.8s ease-out" }}>
          <p style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 6, fontWeight: "bolder", color: "rgba(255,255,255,0.7)", marginBottom: 12 }}>Accommodations</p>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(40px, 6vw, 56px)", fontWeight: 900, marginBottom: 16 }}>Rooms & Cottages</h1>
          <p style={{ color: "rgba(255,255,255,0.8)", fontSize: 17, maxWidth: 600, margin: "0 auto" }}>Choose from our comfortable accommodations designed for your perfect mountain stay</p>
        </div>
      </section>

      {/* Filters */}
      <section style={{ backgroundColor: "#fff", borderBottom: "1px solid #e5e7eb", position: "sticky", top: 64, zIndex: 10, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
        <div style={{ ...containerStyle, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 0 }}>

          {/* Category — underline tab style */}
          <div style={{ display: "flex" }}>
            {activeCategories.map(c => (
              <button key={c.value} onClick={() => setCategoryFilter(c.value)} style={{
                padding: "18px 28px",
                background: "none",
                border: "none",
                borderBottom: categoryFilter === c.value ? "3px solid #16a34a" : "3px solid transparent",
                color: categoryFilter === c.value ? "#16a34a" : "#6b7280",
                fontWeight: categoryFilter === c.value ? 700 : 500,
                fontSize: 15,
                cursor: "pointer",
                transition: "all 0.2s",
                whiteSpace: "nowrap",
                fontFamily: "'Poppins', sans-serif",
              }}>{c.label}</button>
            ))}
          </div>

          {/* Availability — colour-coded pills */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 8px", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 500, marginRight: 4 }}>Availability:</span>
            {[
              { id: "all",       label: "All",       activeColor: "#374151", activeBg: "#f3f4f6",  activeBorder: "#d1d5db" },
              { id: "available", label: "Available",  activeColor: "#15803d", activeBg: "#f0fdf4",  activeBorder: "#86efac" },
              { id: "occupied",  label: "Occupied",   activeColor: "#b91c1c", activeBg: "#fef2f2",  activeBorder: "#fca5a5" },
            ].map(f => (
              <button key={f.id} onClick={() => setStatusFilter(f.id)} style={{
                padding: "5px 14px",
                borderRadius: 9999,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                border: `1.5px solid ${statusFilter === f.id ? f.activeBorder : "#e5e7eb"}`,
                backgroundColor: statusFilter === f.id ? f.activeBg : "transparent",
                color: statusFilter === f.id ? f.activeColor : "#9ca3af",
                transition: "all 0.2s",
                fontFamily: "'Poppins', sans-serif",
              }}>{f.label}</button>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms Grid */}
      <section style={{ ...sectionStyle, backgroundColor: "#f9fafb" }}>
        <div style={containerStyle}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 80 }}>
              <div style={{ width: 32, height: 32, border: "3px solid #e5e7eb", borderTopColor: "#16a34a", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
            </div>
          ) : filteredRooms.length === 0 ? (
            <div style={{ textAlign: "center", padding: 80, color: "#9ca3af" }}>
              <Mountain size={48} style={{ margin: "0 auto 16px", opacity: 0.3 }} />
              <p style={{ fontSize: 16 }}>No rooms found.</p>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 32 }}>
              {filteredRooms.map(room => (
                <div key={room.id}
                  style={{ ...cardStyle, transform: hoveredId === room.id ? "translateY(-10px) scale(1.02)" : "translateY(0) scale(1)", boxShadow: hoveredId === room.id ? "0 20px 50px rgba(0,0,0,0.18)" : "0 4px 20px rgba(0,0,0,0.08)" }}
                  onMouseEnter={() => setHoveredId(room.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  {/* Carousel */}
                  <RoomCarousel
                    images={room.images}
                    roomName={room.name}
                    onOpenLightbox={(startIdx) => setLightbox({ room, startIdx })}
                  />

                  {/* Status badge overlaid on card (outside figure) */}
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", top: -208, right: 16, padding: "4px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, backgroundColor: room.status === "available" ? "#16a34a" : "#f3f4f6", color: room.status === "available" ? "#fff" : "#374151" }}>
                      {room.status}
                    </span>
                  </div>

                  {/* Content */}
                  <div style={{ padding: 24 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: "#1f2937", margin: 0 }}>{room.name}</h3>
                      {room.category && (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 9999, textTransform: "capitalize", flexShrink: 0, ...(CATEGORY_COLORS[room.category] || { bg: "#f3f4f6", color: "#374151" }), backgroundColor: (CATEGORY_COLORS[room.category] || {}).bg }}>
                          {room.category}
                        </span>
                      )}
                    </div>
                    <p style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>{room.description || "A comfortable room for your mountain stay."}</p>

                    <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Users size={14} /> Up to {room.capacity} Guests</span>
                      {room.room_number && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Mountain size={14} /> Room #{room.room_number}</span>}
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                      {(room.amenities || []).slice(0, 4).map((amenity, i) => (
                        <span key={i} style={{ fontSize: 11, padding: "4px 8px", backgroundColor: "#f0fdf4", color: "#166534", borderRadius: 9999, border: "1px solid #86efac", display: "flex", alignItems: "center", gap: 4 }}>
                          <Check size={10} /> {amenity}
                        </span>
                      ))}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
                      <div>
                        <p style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>Starting from</p>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 900, color: "#16a34a" }}>₹{Number(room.price_per_night).toLocaleString()}</span>
                          <span style={{ fontSize: 12, color: "#9ca3af" }}>/night</span>
                        </div>
                      </div>
                      <Link to="/register" style={btnPrimaryStyle}>Book Now</Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Amenities */}
      <section style={{ ...sectionStyle, backgroundColor: "#fff" }}>
        <div style={containerStyle}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <p style={{ fontSize: 11, color: "#16a34a", textTransform: "uppercase", letterSpacing: 4, fontWeight: 600, marginBottom: 12 }}>Complimentary</p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 48, fontWeight: 900, color: "#1f2937" }}>
              All Rooms{" "}
              <span style={{ background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Include</span>
            </h2>
            <div style={{ width: 64, height: 4, background: "#16a34a", borderRadius: 2, margin: "20px auto 0" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 24 }}>
            {amenities.map(({ icon, text }) => (
              <div key={text} style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: 24, borderRadius: 20, backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", transition: "all 0.3s" }}>
                <div style={{ color: "#16a34a", marginBottom: 12 }}>{icon}</div>
                <p style={{ fontSize: 13, fontWeight: 500, color: "#6b7280" }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ ...sectionStyle, backgroundColor: "#f9fafb" }}>
        <div style={{ ...containerStyle, textAlign: "center", maxWidth: 700 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 48, fontWeight: 900, color: "#1f2937", marginBottom: 16 }}>Need Help Choosing?</h2>
          <p style={{ color: "#6b7280", fontSize: 17, marginBottom: 40 }}>Our team is here to help you find the perfect room for your stay</p>
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 16, justifyContent: "center" }}>
            <Link to="/contact" style={{ ...btnPrimaryStyle, padding: "14px 40px", fontSize: 15 }}>Contact Us</Link>
            <Link to="/register" style={{ ...btnPrimaryStyle, backgroundColor: "transparent", color: "#16a34a", border: "2px solid #16a34a", padding: "14px 40px", fontSize: 15 }}>Book Now</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Rooms;
